import { CSSProperties, FC, ReactNode } from 'react';
import { PhoneFlowProducts } from 'state/reducers/shopifyCatalogue';

export enum PurchaseOptionType {
  FULL_PRICE = 'full_price',
  CONTRACT_PLAN = 'contract_plan'
}

/* Sub-options presented within the carrier contract purchase option. */
export enum ContractPlanType {
  LIMITED = 'limited',
  UNLIMITED = 'unlimited'
}

export type PhoneColorOption = {
  key: string;
  label: string;
  swatchHex: string;
  productKey: keyof PhoneFlowProducts;
  variantOptionValue?: string;
  backgroundClassName?: string;
  backgroundStyle?: CSSProperties;
  buttonColor: 'white' | 'black';
  radioColor: 'gray' | 'light';
  arrowColor: 'gray' | 'black';
};

export type ContractPlanConfig = {
  type: ContractPlanType;
  monthlyPrice: number;
  descriptionLabel: string;
} & (
  | { variantId: string; variantMap?: never }
  | { variantMap: Record<string, string>; variantId?: never }
);

// A contract plan's Shopify variant is either a single, color-agnostic
// `variantId` (Light Phone III) or a per-color `variantMap` keyed by the
// color's `variantOptionValue` (Light Flip, whose contract variants bundle the
// selected phone color). Resolve to the right variant for the active color.
export const resolveContractPlanVariantId = (
  plan: ContractPlanConfig,
  variantOptionValue?: string
): string | undefined =>
  plan.variantMap
    ? variantOptionValue
      ? plan.variantMap[variantOptionValue]
      : undefined
    : plan.variantId;

export const planPriceLabel = (plan: ContractPlanConfig): string =>
  `$${plan.monthlyPrice}/month`;

export const planPriceLabelShort = (plan: ContractPlanConfig): string =>
  `$${plan.monthlyPrice}/mo`;

export type PurchaseOptionCopy = {
  contractLabel: (selectedPlan: ContractPlanConfig) => string;
  contractSubheading: ReactNode;
  contractDescription: (deliveryDetails: ReactNode) => ReactNode;
  fullPriceHeading: (priceFormatted: string) => ReactNode;
  fullPriceDescription: ReactNode;
};

export type PhoneModelConfig = {
  key: string;
  displayTitle: string;
  route: string;
  productHandle: string;
  seo: {
    title: string;
    description: string;
    schema?: object;
  };
  colors: PhoneColorOption[];
  defaultColorKey: string;
  // Which resolved FAQ list from globalSettings to render on this model's page.
  faqKey: 'lightIIIFAQ' | 'lightFlipFAQ';
  showColorSelector?: boolean;
  phoneStepButtonLabel: string;
  deliveryEstimate: string;
  contractPlans: ContractPlanConfig[];
  purchaseOptionCopy: PurchaseOptionCopy;
  isPlanPurchaseOptionEnabled: () => boolean;
  ContractTerms: FC;
  operationsProductKey?: string;
};
